import type {
  ApiDeliveryDealList,
  ApiDeliveryPointList,
  ApiDeliveryDealDetail,
  ApiCreateDeliveryOfferPayload,
} from 'src/types/delivery';

import { useQuery, useMutation, queryOptions, useQueryClient } from '@tanstack/react-query';

import axios from 'src/utils/axios';
import { requestSession } from 'src/utils/storefrontSession';
import { isDeliveryTerminal } from 'src/utils/deliveryActions';
import { getApiErrorCode } from 'src/utils/listingAvailability';

import { QueryKeys } from './QueryKeys';
import { useRequestSession } from './useRequestSession';
import { LIVE_STALE_TIME, REFERENCE_STALE_TIME } from './staleness';

/**
 * TanStack hooks for the buyer delivery surface (deal v2, spec §7). All routes are
 * `@Protected({ forWallet, forAccount })`; ownership resolves to the account's primary wallet,
 * and the account session's token travels on every request (`src/utils/axios.ts`).
 *
 * The reads are keyed by the session they are made under (`QueryKeys.forSession`, app#248), so
 * the next person to sign in on a shared device is never shown the previous person's purchases.
 */

/** Active delivery cities (id + city; price is quoted by the dealer on Approve). */
export const useDeliveryPoints = (options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: QueryKeys.deliveryPoints(),
    queryFn: async (): Promise<ApiDeliveryPointList> => {
      const response = await axios.get<ApiDeliveryPointList>('/buyer/delivery/points');
      return response.data;
    },
    staleTime: REFERENCE_STALE_TIME,
    enabled: options?.enabled ?? true,
    // A coded rejection (403 for an account with no linked wallet) is a verdict, not a hiccup:
    // retrying it only delays the explanation the modal shows. Retry once for everything else
    // (network/5xx, which carry no code).
    retry: (failureCount, error) => getApiErrorCode(error) === null && failureCount < 1,
  });

/** The buyer's own delivery deals ({ items, total }, no pagination — like rentals). */
export const useDeliveryDeals = (options?: { enabled?: boolean }) => {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.deliveryDeals(), session),
    queryFn: async (): Promise<ApiDeliveryDealList> => {
      const response = await axios.get<ApiDeliveryDealList>('/buyer/delivery/deals');
      return response.data;
    },
    staleTime: LIVE_STALE_TIME,
    enabled: options?.enabled ?? true,
  });
};

/**
 * A single delivery deal: the row, the timeline the tracker renders and the dispute mirror
 * (contracts v3, api part §4), plus the buyer-only handover code from `escrowed`.
 *
 * Refetched every 3 seconds while the deal is live (UI part §2). The poller behind it ticks every
 * 2 seconds on EVM, so a status the chain has already changed reaches the screen within about five
 * — which is what lets the payment flow end by itself instead of asking anyone to reload. A
 * finished deal is left alone: `isLive` goes false and the polling stops.
 */
export const deliveryDealQuery = (id: string, session: string) =>
  queryOptions({
    queryKey: QueryKeys.forSession(QueryKeys.deliveryDeal(id), session),
    queryFn: async (): Promise<ApiDeliveryDealDetail> => {
      const response = await axios.get<ApiDeliveryDealDetail>(`/buyer/delivery/deals/${id}`);
      return response.data;
    },
  });

export const useDeliveryDeal = (id: string, options?: { enabled?: boolean; poll?: boolean }) => {
  const session = useRequestSession();

  return useQuery({
    ...deliveryDealQuery(id, session),
    enabled: options?.enabled ?? true,
    staleTime: LIVE_STALE_TIME,
    // The interval reads the answer it just got, so a deal that reached a terminal status stops
    // polling on its own — no screen has to hold a flag and keep it in step with the data.
    // `poll: false` is for the readers that only want the deal once (a receipt on a sold listing),
    // where a live escrow would otherwise be re-fetched every three seconds behind a static card.
    refetchInterval: (query) => {
      const status = query.state.data?.status;

      return options?.poll === false || (status && isDeliveryTerminal(status)) ? false : 3000;
    },
    // Coming back to the tab is exactly when a stale escrow status is most visible.
    refetchOnWindowFocus: options?.poll !== false,
  });
};

/** Create a delivery offer (Buy with Delivery). Returns the created deal (status `offered`). */
export const useCreateDeliveryOffer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ApiCreateDeliveryOfferPayload) => {
      const response = await axios.post<ApiDeliveryDealDetail>('/buyer/delivery/deals', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryKeys.deliveryDeals() });
    },
  });
};

/**
 * Shared cache update for the buyer mutations that return the updated deal.
 *
 * None of these three (withdraw, confirm, gas-paid) is a signature — the api moves the deal and
 * deploys the clone — so a buyer who has not connected a wallet in this browser can press them;
 * the on-chain buttons on the same screen go through the wallet.
 *
 * The deal they answer with is the session's that sent them, so it is cached under that session —
 * taken as the request leaves (`onMutate`), not when the answer arrives, when another person may
 * already be signed in on the tab (app#248).
 */
const useDeliveryDealMutation = (path: (id: string) => string) => {
  const queryClient = useQueryClient();

  return useMutation({
    onMutate: () => ({ session: requestSession() }),
    mutationFn: async ({ id, body }: { id: string; body?: unknown }) => {
      const response = await axios.post<ApiDeliveryDealDetail>(path(id), body);
      return response.data;
    },
    onSuccess: (data, _variables, sent) => {
      queryClient.setQueryData(QueryKeys.forSession(QueryKeys.deliveryDeal(data.id), sent.session), data);
      queryClient.invalidateQueries({ queryKey: QueryKeys.deliveryDeals() });
    },
  });
};

/** Withdraw an offer before any money (from `offered`/`approved`/`awaiting_payment`). */
export const useWithdrawDeliveryDeal = () =>
  useDeliveryDealMutation((id) => `/buyer/delivery/deals/${id}/withdraw`);

/** Confirm the offer within the payment window — assigns the logist and starts the deploy. */
export const useConfirmDeliveryDeal = () =>
  useDeliveryDealMutation((id) => `/buyer/delivery/deals/${id}/confirm`);

/** Report the native-TRX gas transfer (verified against the platform wallet, spec §4.4). */
export const useDeliveryGasPaid = () => {
  const mutation = useDeliveryDealMutation((id) => `/buyer/delivery/deals/${id}/gas-paid`);

  return {
    ...mutation,
    reportGasPaid: (id: string, txHash: string) => mutation.mutateAsync({ id, body: { txHash } }),
  };
};

/**
 * Report the transaction the buyer's own wallet created the escrow contract with, from the terms
 * sheet (api#380). The api verifies it on chain and answers the deal with `contractAddress`;
 * 409 `ESCROW_TX_PENDING` until the receipt is deep enough (the dialog retries), 400 for a
 * transaction that is not this deal's creation. Idempotent — the same hash twice is the same answer.
 */
export const useDeliveryEscrowCreated = () => {
  const mutation = useDeliveryDealMutation((id) => `/buyer/delivery/deals/${id}/escrow-created`);

  return {
    ...mutation,
    reportEscrowCreated: (id: string, txHash: string) =>
      mutation.mutateAsync({ id, body: { txHash } }),
  };
};

/**
 * One fresh read of a deal, on demand, through the same query the screen reads — so it lands in
 * the same cache entry, under the same session key, with the same credentials.
 *
 * For the moments a dialog must know the deal *now* rather than as of the last poll: before asking
 * the wallet to create the escrow contract (the poller may have recorded one while the tab was
 * closed), and after the factory refused because the contract already exists (api#380, §2.9).
 */
export const useFetchDeliveryDeal = () => {
  const queryClient = useQueryClient();

  return (id: string): Promise<ApiDeliveryDealDetail> =>
    queryClient.fetchQuery({ ...deliveryDealQuery(id, requestSession()), staleTime: 0 });
};
